import {
  Component,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  inject,
  signal,
} from '@angular/core';

import { User } from '@shared/models/user.model';
import { Friend, Friends } from './friends.model';
import { FriendsService } from './friends.service';
import { RealtimeService } from '@shared/services/realtime.service';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';

import { takeUntil } from 'rxjs/operators';
import { LoggerService } from '@app/shared/services/logger.service';
import { Observable } from 'rxjs';
import { SharedModule } from '@app/shared/shared.module';
import { FriendsSearchComponent } from './friends-search/friends-search.component';
import { FriendsListComponent } from './friends-list/friends-list.component';
import { ParagraphComponent } from '@app/shared/components/text/p/p.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-friends',
  templateUrl: './friends.component.html',
  styleUrls: ['./friends.component.scss'],
  standalone: true,
  imports: [SharedModule, FriendsSearchComponent, FriendsListComponent, ParagraphComponent],
})
export class FriendsComponent implements OnInit, OnChanges {
  @Input() user: User;
  @Input() isSelf: boolean;
  @Output() pendingFriends = new EventEmitter<number>();

  friends = signal<Friends | null>(null);
  componentDestroy: () => Observable<unknown>;

  private readonly friendsService = inject(FriendsService);
  public readonly logger = inject(LoggerService);
  private readonly realtimeService = inject(RealtimeService);

  ngOnInit() {
    this.friendsService.friendRequest$.pipe(takeUntil(this.componentDestroy())).subscribe({
      next: (friend: Friend) => {
        if (friend.id === this.user.id) return;
        this.addPendingFriend(friend);
      },
      error: (error) => this.logger.error(error),
    });

    this.friendsService.acceptFriend$.pipe(takeUntil(this.componentDestroy())).subscribe({
      next: (friend: Friend) => {
        if (friend.id === this.user.id) return;
        this.addFriend(friend);
        if (this.friends()?.pendingFriends.find((x: Friend) => x.id === friend.id)) {
          this.removePendingFriend(friend.id);
        }
      },
      error: (error) => this.logger.error(error),
    });

    this.friendsService.rejectFriend$.pipe(takeUntil(this.componentDestroy())).subscribe({
      next: (pendingFriendId: string) => {
        if (pendingFriendId === this.user.id) return;
        this.removePendingFriend(pendingFriendId);
      },
      error: (error) => this.logger.error(error),
    });

    this.friendsService.unfriend$.pipe(takeUntil(this.componentDestroy())).subscribe({
      next: (friendId: string) => {
        if (friendId === this.user.id) return;
        this.removeFriend(friendId);
      },
      error: (error) => this.logger.error(error),
    });

    this.realtimeService
      .on('AcceptFriendRequest')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.user.id !== data.currentUser.id && this.user.id !== data.friend.id) return;
          const friend = this.user.id === data.currentUser.id ? data.friend : data.currentUser;
          this.addFriend(friend);
        },
        error: (error) => this.logger.error(error),
      });

    this.realtimeService
      .on('Unfriend')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.user.id !== data.currentUserId && this.user.id !== data.friendId) return;
          const friendId = this.user.id === data.currentUserId ? data.friendId : data.currentUserId;
          this.removeFriend(friendId);
        },
        error: (error) => this.logger.error(error),
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.user || !changes.user.currentValue) return;
    if (!changes.user.previousValue || changes.user.currentValue.id !== changes.user.previousValue.id) {
      this.getUserFriends(changes.user.currentValue.id);
    }
  }

  private getUserFriends(userId: string): void {
    this.friendsService
      .getUserFriends(userId)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (friends: Friends) => {
          this.friends.set(friends);
          this.pendingFriends.emit(this.friends()!.pendingFriendsCount);
        },
        error: (error) => this.logger.error(error),
      });
  }

  private addFriend(friend: Friend): void {
    this.friends.update((f) => (f ? { ...f, friendsCount: f.friendsCount + 1, friends: [...f.friends, friend] } : f));
  }

  private removeFriend(friendId: string): void {
    this.friends.update((f) =>
      f
        ? {
            ...f,
            friendsCount: f.friendsCount - 1,
            friends: f.friends.filter((friend: Friend) => friend.id !== friendId),
          }
        : f
    );
  }

  private addPendingFriend(pendingFriend: Friend): void {
    this.friends.update((f) =>
      f
        ? { ...f, pendingFriendsCount: f.pendingFriendsCount + 1, pendingFriends: [...f.pendingFriends, pendingFriend] }
        : f
    );
    this.pendingFriends.emit(this.friends()!.pendingFriendsCount);
  }

  private removePendingFriend(pendingFriendId: string): void {
    this.friends.update((f) =>
      f
        ? {
            ...f,
            pendingFriendsCount: f.pendingFriendsCount - 1,
            pendingFriends: f.pendingFriends.filter((pendingFriend: Friend) => pendingFriend.id !== pendingFriendId),
          }
        : f
    );
    this.pendingFriends.emit(this.friends()!.pendingFriendsCount);
  }
}
